import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { CalendarIcon, Search, Download, Hash, MapPin, BarChart3, TableProperties, Loader2, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import * as XLSX from "xlsx";

// ─── Types ───────────────────────────────────────────────────────────
interface HistoryRow {
  id: string;
  mailing_record_id: string;
  campaign_name: string | null;
  mailed_date: string | null;
  mailing_round: number;
  letter_type: string | null;
  returned: boolean;
  return_reason: string | null;
  response_received: boolean;
  response_date: string | null;
  notes: string | null;
  mailing_records: {
    first_name: string | null;
    last_name: string | null;
    business_name: string | null;
    street_address: string | null;
    city: string | null;
    state: string | null;
    zip_code: string | null;
    county: string | null;
    seller_id: string;
  } | null;
}

type SortField = "mailed_date" | "campaign_name" | "mailing_round" | "last_name" | "county";
type SortDir = "asc" | "desc";

// ─── Component ───────────────────────────────────────────────────────
export function DataExplorer() {
  const [subTab, setSubTab] = useState("history");

  return (
    <div className="space-y-4">
      <Tabs value={subTab} onValueChange={setSubTab}>
        <TabsList>
          <TabsTrigger value="history" className="flex items-center gap-1.5">
            <Search className="h-4 w-4" /> History Browser
          </TabsTrigger>
          <TabsTrigger value="counts" className="flex items-center gap-1.5">
            <Hash className="h-4 w-4" /> Quick Counts
          </TabsTrigger>
          <TabsTrigger value="crosstab" className="flex items-center gap-1.5">
            <TableProperties className="h-4 w-4" /> Cross-Tab
          </TabsTrigger>
        </TabsList>

        <TabsContent value="history">
          <HistoryBrowser />
        </TabsContent>
        <TabsContent value="counts">
          <QuickCounts />
        </TabsContent>
        <TabsContent value="crosstab">
          <CrossTabSummaries />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 1. MAILING HISTORY BROWSER
// ═══════════════════════════════════════════════════════════════════════
function HistoryBrowser() {
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [campaign, setCampaign] = useState("all");
  const [round, setRound] = useState("all");
  const [responseFilter, setResponseFilter] = useState("all");
  const [returnedFilter, setReturnedFilter] = useState("all");
  const [campaigns, setCampaigns] = useState<string[]>([]);

  // Sort
  const [sortField, setSortField] = useState<SortField>("mailed_date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // Pagination
  const [page, setPage] = useState(0);
  const pageSize = 100;

  useEffect(() => {
    loadCampaigns();
  }, []);

  useEffect(() => {
    setPage(0);
    fetchData(0);
  }, [dateFrom, dateTo, campaign, round, responseFilter, returnedFilter, sortField, sortDir]);

  const loadCampaigns = async () => {
    const { data } = await supabase
      .from("mailing_history")
      .select("campaign_name")
      .not("campaign_name", "is", null);
    if (data) {
      const unique = [...new Set(data.map(d => d.campaign_name).filter(Boolean))] as string[];
      setCampaigns(unique.sort());
    }
  };

  const fetchData = useCallback(async (p: number) => {
    setLoading(true);
    try {
      let query = supabase
        .from("mailing_history")
        .select("*, mailing_records!inner(first_name, last_name, business_name, street_address, city, state, zip_code, county, seller_id)", { count: "exact" });

      if (dateFrom) query = query.gte("mailed_date", format(dateFrom, "yyyy-MM-dd"));
      if (dateTo) query = query.lte("mailed_date", format(dateTo, "yyyy-MM-dd"));
      if (campaign !== "all") query = query.eq("campaign_name", campaign);
      if (round !== "all") {
        if (round === "3+") {
          query = query.gte("mailing_round", 3);
        } else {
          query = query.eq("mailing_round", parseInt(round));
        }
      }
      if (responseFilter === "yes") query = query.eq("response_received", true);
      if (responseFilter === "no") query = query.eq("response_received", false);
      if (returnedFilter === "yes") query = query.eq("returned", true);
      if (returnedFilter === "no") query = query.eq("returned", false);

      // Sort
      if (sortField === "last_name") {
        query = query.order("mailing_records(last_name)", { ascending: sortDir === "asc" });
      } else if (sortField === "county") {
        query = query.order("mailing_records(county)", { ascending: sortDir === "asc" });
      } else {
        query = query.order(sortField, { ascending: sortDir === "asc" });
      }

      query = query.range(p * pageSize, (p + 1) * pageSize - 1);

      const { data, count, error } = await query;
      if (error) throw error;
      setRows((data || []) as unknown as HistoryRow[]);
      setTotalCount(count || 0);
    } catch (e: any) {
      toast.error("Failed to load history: " + e.message);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, campaign, round, responseFilter, returnedFilter, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="h-3 w-3 ml-1" /> : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  const exportToExcel = () => {
    const exportData = rows.map(r => ({
      "Seller ID": r.mailing_records?.seller_id || "",
      "Name": r.mailing_records ? [r.mailing_records.first_name, r.mailing_records.last_name].filter(Boolean).join(" ") : "",
      "Business": r.mailing_records?.business_name || "",
      "County": r.mailing_records?.county || "",
      "State": r.mailing_records?.state || "",
      "Campaign": r.campaign_name || "",
      "Mailed Date": r.mailed_date || "",
      "Round": r.mailing_round,
      "Letter Type": r.letter_type || "",
      "Returned": r.returned ? "Yes" : "No",
      "Response": r.response_received ? "Yes" : "No",
      "Response Date": r.response_date || "",
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Mailing History");
    XLSX.writeFile(wb, `mailing-history-export-${format(new Date(), "yyyy-MM-dd")}.xlsx`);
    toast.success(`Exported ${exportData.length} rows`);
  };

  const goPage = (p: number) => {
    setPage(p);
    fetchData(p);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Mailing History Browser</CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-sm">
              {totalCount.toLocaleString()} result{totalCount !== 1 ? "s" : ""}
            </Badge>
            <Button variant="outline" size="sm" onClick={exportToExcel} disabled={rows.length === 0}>
              <Download className="h-4 w-4 mr-1" /> Export
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <Label className="text-xs">Date From</Label>
            <DatePicker date={dateFrom} onSelect={setDateFrom} placeholder="From..." />
          </div>
          <div>
            <Label className="text-xs">Date To</Label>
            <DatePicker date={dateTo} onSelect={setDateTo} placeholder="To..." />
          </div>
          <div>
            <Label className="text-xs">Campaign</Label>
            <Select value={campaign} onValueChange={setCampaign}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Campaigns</SelectItem>
                {campaigns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Round</Label>
            <Select value={round} onValueChange={setRound}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Rounds</SelectItem>
                <SelectItem value="1">Round 1</SelectItem>
                <SelectItem value="2">Round 2</SelectItem>
                <SelectItem value="3+">Round 3+</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Response</Label>
            <Select value={responseFilter} onValueChange={setResponseFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="yes">Responded</SelectItem>
                <SelectItem value="no">No Response</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Returned</Label>
            <Select value={returnedFilter} onValueChange={setReturnedFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="yes">Returned</SelectItem>
                <SelectItem value="no">Not Returned</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Clear filters */}
        {(dateFrom || dateTo || campaign !== "all" || round !== "all" || responseFilter !== "all" || returnedFilter !== "all") && (
          <Button variant="ghost" size="sm" onClick={() => {
            setDateFrom(undefined); setDateTo(undefined); setCampaign("all");
            setRound("all"); setResponseFilter("all"); setReturnedFilter("all");
          }}>
            Clear all filters
          </Button>
        )}

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="rounded-md border overflow-auto max-h-[600px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="cursor-pointer whitespace-nowrap" onClick={() => handleSort("mailed_date")}>
                      Mail Date <SortIcon field="mailed_date" />
                    </TableHead>
                    <TableHead className="cursor-pointer whitespace-nowrap" onClick={() => handleSort("campaign_name")}>
                      Campaign <SortIcon field="campaign_name" />
                    </TableHead>
                    <TableHead className="cursor-pointer whitespace-nowrap" onClick={() => handleSort("mailing_round")}>
                      Round <SortIcon field="mailing_round" />
                    </TableHead>
                    <TableHead className="cursor-pointer whitespace-nowrap" onClick={() => handleSort("last_name")}>
                      Name <SortIcon field="last_name" />
                    </TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead className="cursor-pointer whitespace-nowrap" onClick={() => handleSort("county")}>
                      County <SortIcon field="county" />
                    </TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No history records match your filters
                      </TableCell>
                    </TableRow>
                  ) : rows.map(r => {
                    const rec = r.mailing_records;
                    const name = rec ? [rec.first_name, rec.last_name].filter(Boolean).join(" ") || rec.business_name || "" : "";
                    const addr = rec ? [rec.street_address, rec.city, rec.state, rec.zip_code].filter(Boolean).join(", ") : "";
                    return (
                      <TableRow key={r.id}>
                        <TableCell className="whitespace-nowrap">{r.mailed_date ? format(parseISO(r.mailed_date), "MM/dd/yyyy") : "—"}</TableCell>
                        <TableCell className="max-w-[150px] truncate">{r.campaign_name || "—"}</TableCell>
                        <TableCell>{r.mailing_round}</TableCell>
                        <TableCell className="max-w-[150px] truncate">{name || "—"}</TableCell>
                        <TableCell className="max-w-[200px] truncate text-xs">{addr || "—"}</TableCell>
                        <TableCell>{rec?.county || "—"}</TableCell>
                        <TableCell className="text-xs">{r.letter_type || "—"}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {r.returned && <Badge variant="destructive" className="text-xs">Returned</Badge>}
                            {r.response_received && <Badge className="text-xs bg-green-600">Response</Badge>}
                            {!r.returned && !r.response_received && <span className="text-muted-foreground text-xs">Sent</span>}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalCount > pageSize && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, totalCount)} of {totalCount.toLocaleString()}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => goPage(page - 1)}>Previous</Button>
                  <Button variant="outline" size="sm" disabled={(page + 1) * pageSize >= totalCount} onClick={() => goPage(page + 1)}>Next</Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 2. QUICK COUNTS DASHBOARD
// ═══════════════════════════════════════════════════════════════════════
function QuickCounts() {
  const [lookupDate, setLookupDate] = useState<Date | undefined>();
  const [dateCount, setDateCount] = useState<number | null>(null);
  const [dateLoading, setDateLoading] = useState(false);

  const [statCounts, setStatCounts] = useState<{
    total: number;
    mailed0: number;
    mailed1: number;
    mailed2: number;
    mailed3plus: number;
    withResponse: number;
    scheduled: number;
  } | null>(null);
  const [statLoading, setStatLoading] = useState(true);

  const [byState, setByState] = useState<{ state: string; count: number }[]>([]);
  const [byCounty, setByCounty] = useState<{ county: string; count: number }[]>([]);

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    if (lookupDate) lookupByDate();
  }, [lookupDate]);

  const lookupByDate = async () => {
    if (!lookupDate) return;
    setDateLoading(true);
    const dateStr = format(lookupDate, "yyyy-MM-dd");
    const { count, error } = await supabase
      .from("mailing_history")
      .select("*", { count: "exact", head: true })
      .eq("mailed_date", dateStr);
    setDateCount(error ? null : (count ?? 0));
    setDateLoading(false);
  };

  const loadStats = async () => {
    setStatLoading(true);
    try {
      // Total records
      const { count: total } = await supabase.from("mailing_records").select("*", { count: "exact", head: true });

      // Times mailed breakdowns
      const { count: m0 } = await supabase.from("mailing_records").select("*", { count: "exact", head: true }).eq("times_mailed", 0);
      const { count: m1 } = await supabase.from("mailing_records").select("*", { count: "exact", head: true }).eq("times_mailed", 1);
      const { count: m2 } = await supabase.from("mailing_records").select("*", { count: "exact", head: true }).eq("times_mailed", 2);
      const { count: m3 } = await supabase.from("mailing_records").select("*", { count: "exact", head: true }).gte("times_mailed", 3);

      // With response
      const { count: resp } = await supabase.from("mailing_records").select("*", { count: "exact", head: true }).not("response", "is", null).neq("response", "");

      // Scheduled (future last_mailed_date)
      const today = format(new Date(), "yyyy-MM-dd");
      const { count: sched } = await supabase.from("mailing_history").select("*", { count: "exact", head: true }).gt("mailed_date", today);

      setStatCounts({
        total: total ?? 0,
        mailed0: m0 ?? 0,
        mailed1: m1 ?? 0,
        mailed2: m2 ?? 0,
        mailed3plus: m3 ?? 0,
        withResponse: resp ?? 0,
        scheduled: sched ?? 0,
      });

      // By state
      const { data: stateData } = await supabase.from("mailing_records").select("state");
      if (stateData) {
        const map: Record<string, number> = {};
        stateData.forEach(r => {
          const s = r.state || "Unknown";
          map[s] = (map[s] || 0) + 1;
        });
        setByState(Object.entries(map).map(([state, count]) => ({ state, count })).sort((a, b) => b.count - a.count));
      }

      // By county
      const { data: countyData } = await supabase.from("mailing_records").select("county");
      if (countyData) {
        const map: Record<string, number> = {};
        countyData.forEach(r => {
          const c = r.county || "Unknown";
          map[c] = (map[c] || 0) + 1;
        });
        setByCounty(Object.entries(map).map(([county, count]) => ({ county, count })).sort((a, b) => b.count - a.count).slice(0, 25));
      }
    } catch (e: any) {
      toast.error("Failed to load stats: " + e.message);
    } finally {
      setStatLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Date Lookup */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">📅 Mailings by Date</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4">
            <div>
              <Label className="text-xs">Select a date</Label>
              <DatePicker date={lookupDate} onSelect={setLookupDate} placeholder="Pick a date..." />
            </div>
            {dateLoading && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
            {dateCount !== null && !dateLoading && lookupDate && (
              <div className="text-2xl font-bold">
                {dateCount.toLocaleString()} <span className="text-sm font-normal text-muted-foreground">mailings on {format(lookupDate, "MM/dd/yyyy")}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {statLoading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : statCounts && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <StatCard label="Total Records" value={statCounts.total} />
            <StatCard label="Never Mailed" value={statCounts.mailed0} />
            <StatCard label="Mailed 1×" value={statCounts.mailed1} />
            <StatCard label="Mailed 2×" value={statCounts.mailed2} />
            <StatCard label="Mailed 3+×" value={statCounts.mailed3plus} />
            <StatCard label="With Response" value={statCounts.withResponse} color="text-green-600" />
            <StatCard label="Scheduled" value={statCounts.scheduled} color="text-blue-600" />
          </div>

          {/* By State & County */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-1.5"><MapPin className="h-4 w-4" /> Records by State</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-[300px] overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>State</TableHead>
                        <TableHead className="text-right">Count</TableHead>
                        <TableHead className="text-right">%</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {byState.map(r => (
                        <TableRow key={r.state}>
                          <TableCell>{r.state}</TableCell>
                          <TableCell className="text-right">{r.count.toLocaleString()}</TableCell>
                          <TableCell className="text-right text-muted-foreground">{((r.count / statCounts.total) * 100).toFixed(1)}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-1.5"><BarChart3 className="h-4 w-4" /> Top Counties</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-[300px] overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>County</TableHead>
                        <TableHead className="text-right">Count</TableHead>
                        <TableHead className="text-right">%</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {byCounty.map(r => (
                        <TableRow key={r.county}>
                          <TableCell>{r.county}</TableCell>
                          <TableCell className="text-right">{r.count.toLocaleString()}</TableCell>
                          <TableCell className="text-right text-muted-foreground">{((r.count / statCounts.total) * 100).toFixed(1)}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// 3. CROSS-TAB SUMMARIES
// ═══════════════════════════════════════════════════════════════════════
function CrossTabSummaries() {
  const [activeView, setActiveView] = useState("county-mailed");
  const [loading, setLoading] = useState(false);
  const [countyMailedData, setCountyMailedData] = useState<Record<string, Record<string, number>>>({});
  const [campaignStateData, setCampaignStateData] = useState<Record<string, Record<string, number>>>({});
  const [monthResponseData, setMonthResponseData] = useState<Record<string, { sent: number; responded: number }>>({});

  useEffect(() => {
    if (activeView === "county-mailed") loadCountyMailed();
    if (activeView === "campaign-state") loadCampaignState();
    if (activeView === "month-response") loadMonthResponse();
  }, [activeView]);

  const loadCountyMailed = async () => {
    setLoading(true);
    const { data } = await supabase.from("mailing_records").select("county, times_mailed");
    if (data) {
      const matrix: Record<string, Record<string, number>> = {};
      data.forEach(r => {
        const county = r.county || "Unknown";
        const bucket = r.times_mailed >= 3 ? "3+" : String(r.times_mailed);
        if (!matrix[county]) matrix[county] = { "0": 0, "1": 0, "2": 0, "3+": 0, total: 0 };
        matrix[county][bucket] = (matrix[county][bucket] || 0) + 1;
        matrix[county].total = (matrix[county].total || 0) + 1;
      });
      setCountyMailedData(matrix);
    }
    setLoading(false);
  };

  const loadCampaignState = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("mailing_history")
      .select("campaign_name, mailing_records!inner(state)")
      .not("campaign_name", "is", null);
    if (data) {
      const matrix: Record<string, Record<string, number>> = {};
      (data as any[]).forEach(r => {
        const camp = r.campaign_name || "Unknown";
        const st = r.mailing_records?.state || "Unknown";
        if (!matrix[camp]) matrix[camp] = {};
        matrix[camp][st] = (matrix[camp][st] || 0) + 1;
        matrix[camp]._total = (matrix[camp]._total || 0) + 1;
      });
      setCampaignStateData(matrix);
    }
    setLoading(false);
  };

  const loadMonthResponse = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("mailing_history")
      .select("mailed_date, response_received")
      .not("mailed_date", "is", null);
    if (data) {
      const map: Record<string, { sent: number; responded: number }> = {};
      data.forEach(r => {
        if (!r.mailed_date) return;
        const month = r.mailed_date.substring(0, 7); // YYYY-MM
        if (!map[month]) map[month] = { sent: 0, responded: 0 };
        map[month].sent++;
        if (r.response_received) map[month].responded++;
      });
      setMonthResponseData(map);
    }
    setLoading(false);
  };

  const exportMatrix = (title: string, headers: string[], rows: (string | number)[][]) => {
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, title);
    XLSX.writeFile(wb, `${title.toLowerCase().replace(/\s+/g, "-")}-${format(new Date(), "yyyy-MM-dd")}.xlsx`);
    toast.success("Exported to Excel");
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {[
          { id: "county-mailed", label: "County × Times Mailed" },
          { id: "campaign-state", label: "Campaign × State" },
          { id: "month-response", label: "Month × Response Rate" },
        ].map(v => (
          <Button key={v.id} variant={activeView === v.id ? "default" : "outline"} size="sm" onClick={() => setActiveView(v.id)}>
            {v.label}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <>
          {activeView === "county-mailed" && <CountyMailedMatrix data={countyMailedData} onExport={exportMatrix} />}
          {activeView === "campaign-state" && <CampaignStateMatrix data={campaignStateData} onExport={exportMatrix} />}
          {activeView === "month-response" && <MonthResponseMatrix data={monthResponseData} onExport={exportMatrix} />}
        </>
      )}
    </div>
  );
}

function CountyMailedMatrix({ data, onExport }: { data: Record<string, Record<string, number>>; onExport: (t: string, h: string[], r: (string | number)[][]) => void }) {
  const sorted = Object.entries(data).sort((a, b) => (b[1].total || 0) - (a[1].total || 0));
  const cols = ["0", "1", "2", "3+"];
  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm">County × Times Mailed</CardTitle>
        <Button variant="outline" size="sm" onClick={() => {
          const headers = ["County", ...cols, "Total"];
          const rows = sorted.map(([county, counts]) => [county, ...cols.map(c => counts[c] || 0), counts.total || 0]);
          onExport("County Times Mailed", headers, rows);
        }}>
          <Download className="h-4 w-4 mr-1" /> Export
        </Button>
      </CardHeader>
      <CardContent>
        <div className="max-h-[500px] overflow-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>County</TableHead>
                {cols.map(c => <TableHead key={c} className="text-right">{c}×</TableHead>)}
                <TableHead className="text-right font-bold">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map(([county, counts]) => (
                <TableRow key={county}>
                  <TableCell className="font-medium">{county}</TableCell>
                  {cols.map(c => <TableCell key={c} className="text-right">{(counts[c] || 0).toLocaleString()}</TableCell>)}
                  <TableCell className="text-right font-bold">{(counts.total || 0).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function CampaignStateMatrix({ data, onExport }: { data: Record<string, Record<string, number>>; onExport: (t: string, h: string[], r: (string | number)[][]) => void }) {
  const allStates = [...new Set(Object.values(data).flatMap(v => Object.keys(v).filter(k => k !== "_total")))].sort();
  const sorted = Object.entries(data).sort((a, b) => (b[1]._total || 0) - (a[1]._total || 0));
  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm">Campaign × State</CardTitle>
        <Button variant="outline" size="sm" onClick={() => {
          const headers = ["Campaign", ...allStates, "Total"];
          const rows = sorted.map(([camp, counts]) => [camp, ...allStates.map(s => counts[s] || 0), counts._total || 0]);
          onExport("Campaign State", headers, rows);
        }}>
          <Download className="h-4 w-4 mr-1" /> Export
        </Button>
      </CardHeader>
      <CardContent>
        <div className="max-h-[500px] overflow-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign</TableHead>
                {allStates.map(s => <TableHead key={s} className="text-right">{s}</TableHead>)}
                <TableHead className="text-right font-bold">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map(([camp, counts]) => (
                <TableRow key={camp}>
                  <TableCell className="font-medium max-w-[200px] truncate">{camp}</TableCell>
                  {allStates.map(s => <TableCell key={s} className="text-right">{(counts[s] || 0).toLocaleString()}</TableCell>)}
                  <TableCell className="text-right font-bold">{(counts._total || 0).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function MonthResponseMatrix({ data, onExport }: { data: Record<string, { sent: number; responded: number }>; onExport: (t: string, h: string[], r: (string | number)[][]) => void }) {
  const sorted = Object.entries(data).sort((a, b) => a[0].localeCompare(b[0]));
  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm">Month × Response Rate</CardTitle>
        <Button variant="outline" size="sm" onClick={() => {
          const headers = ["Month", "Sent", "Responded", "Rate %"];
          const rows = sorted.map(([month, v]) => [month, v.sent, v.responded, v.sent > 0 ? ((v.responded / v.sent) * 100).toFixed(1) : "0"]);
          onExport("Month Response Rate", headers, rows);
        }}>
          <Download className="h-4 w-4 mr-1" /> Export
        </Button>
      </CardHeader>
      <CardContent>
        <div className="max-h-[500px] overflow-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead className="text-right">Sent</TableHead>
                <TableHead className="text-right">Responded</TableHead>
                <TableHead className="text-right">Rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map(([month, v]) => (
                <TableRow key={month}>
                  <TableCell className="font-medium">{month}</TableCell>
                  <TableCell className="text-right">{v.sent.toLocaleString()}</TableCell>
                  <TableCell className="text-right">{v.responded.toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    {v.sent > 0 ? ((v.responded / v.sent) * 100).toFixed(1) + "%" : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SHARED: Date Picker
// ═══════════════════════════════════════════════════════════════════════
function DatePicker({ date, onSelect, placeholder }: { date?: Date; onSelect: (d: Date | undefined) => void; placeholder: string }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn("w-full justify-start text-left font-normal h-10", !date && "text-muted-foreground")}>
          <CalendarIcon className="h-4 w-4 mr-2" />
          {date ? format(date, "MM/dd/yyyy") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={date} onSelect={onSelect} initialFocus className="p-3 pointer-events-auto" />
      </PopoverContent>
    </Popover>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <Card>
      <CardContent className="p-4 text-center">
        <div className={cn("text-2xl font-bold", color)}>{value.toLocaleString()}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </CardContent>
    </Card>
  );
}
